import { isArray, snapshotArray } from '../array.js';
import type * as Core from '../../types/calculation-core.js';
import type { PowerRootValue } from '../../../source/shared/calculation-types.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
function mathMetadata(calculatorId: string, title: string, url: string, note: string): Core.CoreMetadata {
    return {
        calculatorId,
        version: '1.0.0',
        effectiveFrom: '2026-08-20',
        lastVerifiedAt: '2026-08-20',
        sources: [{
                id: `openstax-${calculatorId}`,
                title,
                publisher: 'OpenStax',
                url,
                verifiedAt: '2026-08-20',
            }],
        notes: [note],
    };
}
function validateStaticConfig(config: Core.BaseConfig): Core.InputError[] {
    const errors = [...validateMetadata(config.metadata)];
    if (!isFiniteNumber(config.maxAbsoluteInput) || config.maxAbsoluteInput <= 0) {
        errors.push({ field: 'config.maxAbsoluteInput', code: 'INVALID_CONFIG', message: 'Azami giriş sınırı pozitif ve sonlu olmalıdır.' });
    }
    return errors;
}
function configResult(config: Core.BaseConfig, fallback: Core.BaseConfig) {
    const metadata = resolveMetadata(config?.metadata, fallback.metadata);
    if (!config || typeof config !== 'object') {
        return {
            metadata,
            errors: [{ field: 'config', code: 'INVALID_CONFIG', message: 'Hesaplama konfigürasyonu geçersiz.' }],
        };
    }
    return { metadata, errors: validateStaticConfig(config) };
}
function finiteNumbers(values: readonly number[]) {
    return values.every(Number.isFinite);
}
export const DEFAULT_GEOMETRY_CONFIG = {
    metadata: mathMetadata('geometry', 'Prealgebra 2e — Geometry', 'https://openstax.org/books/prealgebra-2e/pages/9-introduction', 'On şekil Öklid geometrisinin temel alan, çevre, hacim ve yüzey formülleriyle hesaplanır.'),
    maxAbsoluteInput: 1e9,
};
const GEOMETRY_FIELDS: Readonly<Record<Core.GeometryShape, readonly string[]>> = {
    square: ['a'],
    rectangle: ['a', 'b'],
    triangle: ['base', 'height'],
    circle: ['radius'],
    trapezoid: ['a', 'b', 'height'],
    parallelogram: ['base', 'side', 'height'],
    cube: ['a'],
    sphere: ['radius'],
    cylinder: ['radius', 'height'],
    cone: ['radius', 'height'],
};
export function calculateGeometry(input: Core.GeometryInput, config: Core.BaseConfig = DEFAULT_GEOMETRY_CONFIG) {
    const checked = configResult(config, DEFAULT_GEOMETRY_CONFIG);
    if (checked.errors.length > 0)
        return failure(checked.errors, checked.metadata);
    if (!input || typeof input !== 'object' || !input.dimensions || typeof input.dimensions !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Şekil ve ölçüler gereklidir.' }], checked.metadata);
    }
    const fields = GEOMETRY_FIELDS[input.shape];
    if (!fields) {
        return failure([{ field: 'shape', code: 'UNSUPPORTED_VALUE', message: 'Geometrik şekil desteklenmiyor.' }], checked.metadata);
    }
    const errors = [];
    for (const field of fields) {
        const value = input.dimensions[field];
        if (!isFiniteNumber(value)) {
            errors.push({ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` });
        }
        else if (value <= 0 || value > config.maxAbsoluteInput) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} sıfırdan büyük ve izin verilen sınırda olmalıdır.` });
        }
    }
    if (input.shape === 'parallelogram' && input.dimensions.height > input.dimensions.side) {
        errors.push({ field: 'height', code: 'OUT_OF_RANGE', message: 'Paralelkenarın yüksekliği yan kenarından büyük olamaz.' });
    }
    if (input.shape === 'trapezoid') {
        const sideCProvided = input.dimensions.sideC !== undefined;
        const sideDProvided = input.dimensions.sideD !== undefined;
        if (sideCProvided !== sideDProvided) {
            errors.push({ field: 'sideC,sideD', code: 'REQUIRED', message: 'Yamuk çevresi için iki yan kenar birlikte girilmelidir.' });
        }
        else if (sideCProvided && sideDProvided) {
            for (const field of ['sideC', 'sideD']) {
                const value = input.dimensions[field];
                if (!isFiniteNumber(value)) {
                    errors.push({ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` });
                }
                else if (value <= 0 || value > config.maxAbsoluteInput) {
                    errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} sıfırdan büyük ve izin verilen sınırda olmalıdır.` });
                }
            }
        }
    }
    if (errors.length > 0)
        return failure(errors, checked.metadata);
    const d = input.dimensions;
    let dimension: '2d' | '3d' = '2d';
    let primaryMetric = 'area';
    let metrics: Record<string, number>;
    switch (input.shape) {
        case 'square':
            metrics = { area: d.a ** 2, perimeter: 4 * d.a, diagonal: d.a * Math.SQRT2 };
            break;
        case 'rectangle':
            metrics = { area: d.a * d.b, perimeter: 2 * (d.a + d.b), diagonal: Math.hypot(d.a, d.b) };
            break;
        case 'triangle':
            metrics = { area: (d.base * d.height) / 2 };
            break;
        case 'circle':
            metrics = { area: Math.PI * d.radius ** 2, circumference: 2 * Math.PI * d.radius, diameter: 2 * d.radius };
            break;
        case 'trapezoid':
            metrics = { area: ((d.a + d.b) * d.height) / 2 };
            if (d.sideC !== undefined && d.sideD !== undefined) {
                metrics.perimeter = d.a + d.b + d.sideC + d.sideD;
            }
            break;
        case 'parallelogram':
            metrics = { area: d.base * d.height, perimeter: 2 * (d.base + d.side) };
            break;
        case 'cube':
            dimension = '3d';
            primaryMetric = 'volume';
            metrics = { volume: d.a ** 3, surfaceArea: 6 * d.a ** 2 };
            break;
        case 'sphere':
            dimension = '3d';
            primaryMetric = 'volume';
            metrics = { volume: (4 / 3) * Math.PI * d.radius ** 3, surfaceArea: 4 * Math.PI * d.radius ** 2 };
            break;
        case 'cylinder':
            dimension = '3d';
            primaryMetric = 'volume';
            metrics = {
                volume: Math.PI * d.radius ** 2 * d.height,
                lateralArea: 2 * Math.PI * d.radius * d.height,
                surfaceArea: 2 * Math.PI * d.radius * (d.radius + d.height),
            };
            break;
        case 'cone': {
            dimension = '3d';
            primaryMetric = 'volume';
            const slantHeight = Math.hypot(d.radius, d.height);
            metrics = {
                volume: (Math.PI * d.radius ** 2 * d.height) / 3,
                slantHeight,
                lateralArea: Math.PI * d.radius * slantHeight,
                surfaceArea: Math.PI * d.radius * (d.radius + slantHeight),
            };
            break;
        }
    }
    const metricValues = Object.values(metrics);
    if (!finiteNumbers(metricValues) || metricValues.some((value) => value <= 0)) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Geometri sonucu temsil edilebilir sayı aralığının dışında.' }], checked.metadata);
    }
    return success({
        shape: input.shape,
        dimension,
        primaryMetric,
        primaryValue: metrics[primaryMetric],
        metrics,
    }, checked.metadata);
}
export const DEFAULT_PRIME_CONFIG = {
    metadata: mathMetadata('prime-number', 'Prealgebra 2e — Prime Factorization and the Least Common Multiple', 'https://openstax.org/books/prealgebra-2e/pages/2-5-prime-factorization-and-the-least-common-multiple', '1–10^15 aralığında deterministik Miller–Rabin ve tamsayı çarpanlara ayırma kullanılır.'),
    maxAbsoluteInput: 1e15,
    maxInteger: '1000000000000000',
};
export const DEFAULT_GCD_LCM_CONFIG = {
    metadata: mathMetadata('gcd-lcm', 'Prealgebra 2e — Greatest Common Factor and Factor by Grouping', 'https://openstax.org/books/prealgebra-2e/pages/7-1-greatest-common-factor-and-factor-by-grouping', 'EBOB Öklid algoritmasıyla, EKOK taşmasız BigInt çarpımıyla hesaplanır.'),
    maxAbsoluteInput: 1e12,
    maxInteger: '1000000000000',
};
function validateIntegerConfig(config: Core.BaseConfig & {
    readonly maxInteger: string;
}): Core.InputError[] {
    const errors = [...validateStaticConfig(config)];
    const maxAbsoluteInputIsSafeInteger = Number.isSafeInteger(config.maxAbsoluteInput)
        && config.maxAbsoluteInput > 0;
    if (isFiniteNumber(config.maxAbsoluteInput)
        && config.maxAbsoluteInput > 0
        && !Number.isSafeInteger(config.maxAbsoluteInput)) {
        errors.push({ field: 'config.maxAbsoluteInput', code: 'INVALID_CONFIG', message: 'Tamsayı araçlarında azami giriş sınırı güvenli bir tamsayı olmalıdır.' });
    }
    if (typeof config.maxInteger !== 'string' || !/^[1-9]\d*$/.test(config.maxInteger)) {
        errors.push({ field: 'config.maxInteger', code: 'INVALID_CONFIG', message: 'Tamsayı sınırı pozitif bir tamsayı olmalıdır.' });
    }
    else if (maxAbsoluteInputIsSafeInteger
        && BigInt(config.maxInteger) > BigInt(config.maxAbsoluteInput)) {
        errors.push({ field: 'config.maxInteger', code: 'INVALID_CONFIG', message: 'Tamsayı sınırı ortak azami giriş sınırını aşamaz.' });
    }
    return errors;
}
function parsePositiveInteger(value: unknown, max: bigint, field: string) {
    if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) {
        return { error: { field, code: 'OUT_OF_RANGE', message: 'Pozitif tamsayı yalnız rakamlarla girilmelidir.' } };
    }
    const canonical = value.trim().replace(/^0+/, '') || '0';
    const maxText = max.toString();
    if (canonical === '0'
        || canonical.length > maxText.length
        || (canonical.length === maxText.length && canonical > maxText)) {
        return { error: { field, code: 'OUT_OF_RANGE', message: 'Tamsayı izin verilen aralığın dışındadır.' } };
    }
    return { value: BigInt(canonical) };
}
function bigintGcd(a: bigint, b: bigint) {
    let x = a < 0n ? -a : a;
    let y = b < 0n ? -b : b;
    while (y !== 0n)
        [x, y] = [y, x % y];
    return x;
}
function modPow(base: bigint, exponent: bigint, modulus: bigint) {
    let result = 1n;
    let value = base % modulus;
    let power = exponent;
    while (power > 0n) {
        if (power & 1n)
            result = (result * value) % modulus;
        value = (value * value) % modulus;
        power >>= 1n;
    }
    return result;
}
const MILLER_RABIN_64 = [2n, 325n, 9375n, 28178n, 450775n, 9780504n, 1795265022n];
function isPrime(n: bigint) {
    if (n < 2n)
        return false;
    for (const prime of [2n, 3n, 5n, 7n, 11n, 13n, 17n, 19n, 23n, 29n, 31n, 37n]) {
        if (n === prime)
            return true;
        if (n % prime === 0n)
            return false;
    }
    let d = n - 1n;
    let s = 0;
    while ((d & 1n) === 0n) {
        d >>= 1n;
        s += 1;
    }
    witness: for (const rawWitness of MILLER_RABIN_64) {
        const witness = rawWitness % n;
        if (witness === 0n)
            continue;
        let x = modPow(witness, d, n);
        if (x === 1n || x === n - 1n)
            continue;
        for (let r = 1; r < s; r += 1) {
            x = (x * x) % n;
            if (x === n - 1n)
                continue witness;
        }
        return false;
    }
    return true;
}
function pollardRho(n: bigint): bigint {
    if (n % 2n === 0n)
        return 2n;
    if (n % 3n === 0n)
        return 3n;
    for (let attempt = 1n; attempt <= 64n; attempt += 1n) {
        let x = 2n + attempt;
        let y = x;
        let divisor = 1n;
        const c = attempt;
        for (let iteration = 0; iteration < 250000 && divisor === 1n; iteration += 1) {
            x = (x * x + c) % n;
            y = (y * y + c) % n;
            y = (y * y + c) % n;
            divisor = bigintGcd(x > y ? x - y : y - x, n);
        }
        if (divisor > 1n && divisor < n)
            return divisor;
    }
    throw new Error('factorization-limit');
}
function factorInteger(n: bigint, output: Map<bigint, number>): void {
    if (n === 1n)
        return;
    if (isPrime(n)) {
        output.set(n, (output.get(n) ?? 0) + 1);
        return;
    }
    const divisor = pollardRho(n);
    factorInteger(divisor, output);
    factorInteger(n / divisor, output);
}
function divisorCountOf(factors: ReadonlyMap<bigint, number>) {
    let count = 1n;
    for (const exponent of factors.values())
        count *= BigInt(exponent + 1);
    return count;
}
function adjacentPrime(n: bigint, direction: 1): bigint;
function adjacentPrime(n: bigint, direction: -1): bigint | null;
function adjacentPrime(n: bigint, direction: -1 | 1) {
    if (direction < 0 && n <= 2n)
        return null;
    let candidate = n + BigInt(direction);
    if (candidate === 2n)
        return 2n;
    if ((candidate & 1n) === 0n)
        candidate += BigInt(direction);
    while (candidate >= 2n && !isPrime(candidate))
        candidate += 2n * BigInt(direction);
    return candidate >= 2n ? candidate : null;
}
export function calculatePrime(input: Core.PrimeInput, config: Core.BaseConfig & {
    readonly maxInteger: string;
} = DEFAULT_PRIME_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_PRIME_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateIntegerConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Asal sayı konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    const parsed = parsePositiveInteger(input?.value, BigInt(config.maxInteger), 'value');
    if (parsed.error || parsed.value === undefined)
        return failure([parsed.error], metadata);
    const value = parsed.value;
    if (value === 1n) {
        return success({ value: '1', classification: 'unit', factors: [], divisorCount: '1', previousPrime: null, nextPrime: '2' }, metadata);
    }
    try {
        const factors = new Map<bigint, number>();
        factorInteger(value, factors);
        const sortedFactors = [...factors.entries()]
            .sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
            .map(([prime, exponent]) => ({ prime: prime.toString(), exponent }));
        const prime = isPrime(value);
        return success({
            value: value.toString(),
            classification: prime ? 'prime' : 'composite',
            factors: sortedFactors,
            divisorCount: divisorCountOf(factors).toString(),
            previousPrime: adjacentPrime(value, -1)?.toString() ?? null,
            nextPrime: adjacentPrime(value, 1).toString(),
        }, metadata);
    }
    catch {
        return failure([{ field: 'value', code: 'NO_CONVERGENCE', message: 'Sayı güvenli süre sınırında çarpanlarına ayrılamadı.' }], metadata);
    }
}
export function calculateGcdLcm(input: Core.GcdLcmInput, config: Core.BaseConfig & {
    readonly maxInteger: string;
} = DEFAULT_GCD_LCM_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_GCD_LCM_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateIntegerConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'EBOB/EKOK konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    const values = input?.values;
    if (!isArray(values) || values.length < 2 || values.length > 6) {
        return failure([{ field: 'values', code: 'OUT_OF_RANGE', message: 'İki ile altı tamsayı gereklidir.' }], metadata);
    }
    const max = BigInt(config.maxInteger);
    const errors: Core.InputError[] = [];
    const numbers: bigint[] = [];
    const entries = snapshotArray(values, (value, index) => {
        const parsed = parsePositiveInteger(value, max, `values.${index}`);
        if (parsed.error)
            errors.push(parsed.error);
        else
            numbers.push(parsed.value);
    });
    if (!entries)
        return failure([{ field: 'values', code: 'OUT_OF_RANGE', message: 'İki ile altı tamsayı gereklidir.' }], metadata);
    if (errors.length > 0)
        return failure(errors, metadata);
    const gcd = numbers.reduce((current, value) => bigintGcd(current, value));
    const lcm = numbers.reduce((current, value) => (current / bigintGcd(current, value)) * value);
    return success({ values: numbers.map(String), gcd: gcd.toString(), lcm: lcm.toString() }, metadata);
}
function exactFactorial(n: number) {
    let result = 1n;
    for (let value = 2; value <= n; value += 1)
        result *= BigInt(value);
    return result;
}
function exactDoubleFactorial(n: number) {
    let result = 1n;
    for (let value = n; value >= 2; value -= 2)
        result *= BigInt(value);
    return result;
}
const POWER_ROOT_METADATA = mathMetadata('power-root', 'Algebra and Trigonometry 2e — Exponential and Logarithmic Functions', 'https://openstax.org/books/algebra-and-trigonometry-2e/pages/6-introduction-to-exponential-and-logarithmic-functions', 'Reel sayı kapsamındaki üs, kök, logaritma ve mutlak değer işlemleri; faktöriyel alt modu tam tamsayı, çift faktöriyel, basamak ve bilimsel gösterim üretir.');
export const DEFAULT_POWER_ROOT_CONFIG = {
    metadata: {
        ...POWER_ROOT_METADATA,
        version: '2.0.0',
        effectiveFrom: '2026-08-22',
        lastVerifiedAt: '2026-08-22',
        sources: [
            { ...POWER_ROOT_METADATA.sources[0], verifiedAt: '2026-08-22' },
            {
                id: 'nist-dlmf-factorial',
                title: 'NIST DLMF — Common Notations and Definitions',
                publisher: 'NIST Digital Library of Mathematical Functions',
                url: 'https://dlmf.nist.gov/front/introduction',
                verifiedAt: '2026-08-22',
            },
        ],
    },
    maxAbsoluteInput: 1e12,
    maxN: 170,
};
function factorialRatio(n: number, stopExclusive: number) {
    let result = 1n;
    for (let value = n; value > stopExclusive; value -= 1)
        result *= BigInt(value);
    return result;
}
export function calculatePowerRoot(input: Core.PowerRootInput, config: Core.BaseConfig & {
    readonly maxN: number;
} = DEFAULT_POWER_ROOT_CONFIG): Core.Calculation<PowerRootValue> {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_POWER_ROOT_CONFIG.metadata);
    const errors = config && typeof config === 'object' ? [...validateStaticConfig(config)] : [
        { field: 'config', code: 'INVALID_CONFIG', message: 'Üs/kök konfigürasyonu geçersiz.' },
    ];
    if (!Number.isInteger(config?.maxN) || config.maxN < 0 || config.maxN > 10000) {
        errors.push({ field: 'config.maxN', code: 'INVALID_CONFIG', message: 'Faktöriyel üst sınırı geçersiz.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    if (!input || typeof input !== 'object' || !isFiniteNumber(input.x) || Math.abs(input.x) > config.maxAbsoluteInput) {
        return failure([{ field: 'x', code: 'OUT_OF_RANGE', message: 'x sonlu ve izin verilen aralıkta olmalıdır.' }], metadata);
    }
    const mode = input.mode;
    if (!['power', 'sqrt', 'nth-root', 'logarithm', 'factorial', 'absolute'].includes(mode)) {
        return failure([{ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'İşlem türü desteklenmiyor.' }], metadata);
    }
    let output: PowerRootValue;
    switch (mode) {
        case 'power': {
            const n = input.n;
            if (typeof n !== 'number' || !isFiniteNumber(n)
                || Math.abs(n) > config.maxAbsoluteInput
                || (input.x === 0 && n === 0)) {
                return failure([{ field: 'n', code: 'OUT_OF_RANGE', message: 'Üs sonlu, izin verilen aralıkta olmalı ve 0^0 seçilmemelidir.' }], metadata);
            }
            output = { mode, value: Math.pow(input.x, n) };
            break;
        }
        case 'sqrt':
            if (input.x < 0)
                return failure([{ field: 'x', code: 'OUT_OF_RANGE', message: 'Negatif sayının reel karekökü yoktur.' }], metadata);
            output = { mode, value: Math.sqrt(input.x) };
            break;
        case 'nth-root': {
            const n = input.n;
            if (typeof n !== 'number' || !isFiniteNumber(n) || Math.abs(n) > config.maxAbsoluteInput || n === 0) {
                return failure([{ field: 'n', code: 'OUT_OF_RANGE', message: 'Kök derecesi sonlu, izin verilen aralıkta ve sıfırdan farklı olmalıdır.' }], metadata);
            }
            if (input.x < 0 && (!Number.isInteger(n) || Math.abs(n % 2) !== 1)) {
                return failure([{ field: 'x', code: 'OUT_OF_RANGE', message: 'Negatif sayının bu derecede reel kökü yoktur.' }], metadata);
            }
            const value = input.x < 0 ? -Math.pow(-input.x, 1 / n) : Math.pow(input.x, 1 / n);
            output = { mode, value };
            break;
        }
        case 'logarithm': {
            const base = input.n ?? 10;
            if (input.x <= 0
                || !isFiniteNumber(base)
                || (input.n !== undefined && Math.abs(base) > config.maxAbsoluteInput)
                || base <= 0
                || base === 1) {
                return failure([{ field: 'n', code: 'OUT_OF_RANGE', message: 'Logaritmada x>0, taban>0 ve taban≠1 olmalıdır.' }], metadata);
            }
            output = { mode, value: Math.log(input.x) / Math.log(base) };
            break;
        }
        case 'factorial': {
            if (!Number.isInteger(input.x) || input.x < 0 || input.x > config.maxN) {
                return failure([{ field: 'x', code: 'OUT_OF_RANGE', message: `Faktöriyel girdisi 0–${config.maxN} tamsayısı olmalıdır.` }], metadata);
            }
            const value = exactFactorial(input.x).toString();
            const exponent = value.length - 1;
            const significant = value.slice(0, 5).padEnd(5, '0');
            const scientific = {
                mantissa: `${significant[0]}.${significant.slice(1)}`,
                exponent,
            };
            const factorialDetails = {
                doubleFactorial: exactDoubleFactorial(input.x).toString(),
                digits: value.length,
                scientific,
            };
            if (input.n === undefined) {
                output = { mode, value, ...factorialDetails };
            }
            else {
                if (!Number.isInteger(input.n) || input.n < 0 || input.n > input.x) {
                    return failure([{ field: 'n', code: 'OUT_OF_RANGE', message: 'r, 0 ile n arasında tamsayı olmalıdır.' }], metadata);
                }
                const permutation = factorialRatio(input.x, input.x - input.n);
                const combination = permutation / exactFactorial(input.n);
                output = {
                    mode,
                    value,
                    ...factorialDetails,
                    combination: combination.toString(),
                    permutation: permutation.toString(),
                };
            }
            break;
        }
        case 'absolute':
            output = {
                mode,
                value: Math.abs(input.x),
                sign: input.x > 0 ? 'positive' : input.x < 0 ? 'negative' : 'zero',
            };
            break;
    }
    if (typeof output.value === 'number') {
        const nonzeroResultCollapsed = output.value === 0
            && input.x !== 0
            && (mode === 'power' || mode === 'nth-root');
        if (!Number.isFinite(output.value) || nonzeroResultCollapsed) {
            return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Sonuç temsil edilebilir sayı aralığının dışında.' }], metadata);
        }
    }
    return success(output, metadata);
}
